import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon, PencilIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const FileDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const [file, setFile] = useState(null);

  useEffect(() => {
    fetchFile();
  }, [id]);

  const fetchFile = async () => {
    const { data } = await get(`/api/files/${id}`);
    if (data) setFile(data);
  };

  const handleDownload = () => {
    if (file?.path) {
      window.open(file.path, '_blank');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  if (loading) return <LoadingSpinner />;
  if (!file) return <div className="p-6">File not found</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/files/filter')}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">File Details</h1>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleDownload}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            <span>Download</span>
          </button>
          <button
            onClick={() => navigate(`/files/edit/${id}`)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
          >
            <PencilIcon className="h-5 w-5" />
            <span>Edit</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-gray-500">ID</label>
            <p className="text-lg font-medium">{file.id}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">File Name</label>
            <p className="text-lg font-medium">{file.path?.split('/').pop() || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">File Type</label>
            <p className="text-lg font-medium">{file.type?.fileName || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Type Description</label>
            <p className="text-lg font-medium">{file.type?.fileDescription || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">File Size</label>
            <p className="text-lg font-medium">{formatFileSize(file.size)}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">File Hash</label>
            <p className="text-lg font-medium font-mono text-sm">{file.hash || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Encryption Status</label>
            <p className="text-lg font-medium">
              <span className={`px-2 py-1 rounded-full text-xs ${
                file.isEncrypted ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
              }`}>
                {file.isEncrypted ? 'Encrypted' : 'Not Encrypted'}
              </span>
            </p>
          </div>
          {file.isEncrypted && (
            <div>
              <label className="text-sm text-gray-500">Encryption Key ID</label>
              <p className="text-lg font-medium font-mono text-sm">{file.encryptionKeyId || '-'}</p>
            </div>
          )}
          <div className="md:col-span-2">
            <label className="text-sm text-gray-500">File Path</label>
            <p className="text-lg font-medium font-mono text-sm break-all">{file.path}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Timestamp</label>
            <p className="text-lg font-medium">
              {file.timeStamp ? new Date(file.timeStamp).toLocaleString() : '-'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileDetails;